import type { CubeApi } from '@cubejs-client/core';
import { ActionIcon, Text, Tooltip } from '@mantine/core';
import { IconInfoCircle, IconSettings } from '@tabler/icons-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import { uuidv7 } from 'uuidv7';
import { resolveLayout, type ResolvedWidget } from '../autoGrid';
import {
  canvasHeightPx, computeGridGeometry, pxToCol, pxToRow, pxToSpanX, pxToSpanY, widgetRectPx
} from '../gridGeometry';
import { compact, placeWithoutMoving, swapCandidate, type PlacementRect } from '../placement';
import { useContainerWidth } from '../useContainerWidth';
import { WidgetLayout } from '../types';
import WidgetCard from '../WidgetCard/WidgetCard';
import { resolveWidgetStepTint } from '../WidgetCard/tints';
import {
  WIDGET_FRAME_PROPS, WIDGET_RESIZE_HANDLE_STYLES, WidgetCardActions, widgetResizeHandles
} from '../WidgetCard/WidgetCardChrome';
import type { WidgetTint } from '../WidgetCard/tints';
import WidgetInspector, { type WidgetInspectorPane } from '../WidgetInspector/WidgetInspector';
import { MobileWidget, WebWidget } from '../Widgets';
import type { WidgetDataState } from '../Widgets/widgetData';
import { Widget, WidgetDefinition, WidgetType } from '../Widgets/types';
import { createWidget } from '../Widgets/WidgetBuilder/WidgetConstructors';
import { useCubeMeta } from '../Widgets/WidgetBuilder/useCubeMeta';
import { presetDefinitionFor } from '../widgetPresets';
import DeleteConfirmationModal from './DeleteConfirmationModal/DeleteConfirmationModal';
import classes from './WidgetLayoutEditor.module.css';
import WidgetToolbar from './WidgetToolbar/WidgetToolbar';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';

/**
 * Narrowest canvas the editor will let you edit on, in unscaled CSS px.
 *
 * ⚠️ THIS NUMBER IS THE CANVAS, NOT THE WINDOW, AND IT SHIPPED WRONG ONCE.
 *
 * It was 1024, chosen in the SDD without measuring anything. The canvas is the
 * window MINUS the reports side panel (~300px) MINUS the inspector (348px), so
 * at a 1280px viewport the real canvas is ~635px — and 1024 turned the editor
 * off on an ordinary laptop. Worse, `canEdit` gates the palette, the resize
 * handles, card selection and the whole inspector, so a single false boolean
 * made every feature in TV1-2291 unreachable while looking like nothing had
 * been built.
 *
 * The gate's only job is to decline editing where a drag is genuinely
 * unworkable — a phone in portrait. It is NOT a data guard: the protection
 * against a narrow-viewport drag rewriting the authored layout is the
 * authored/display split (SDD K3), which is structural and tested. So this
 * threshold must stay small enough that it never fires on a real editing
 * device, and any doubt resolves in favour of letting the user edit.
 */
export const EDIT_MIN_CANVAS_PX = 480;

/**
 * How long the editor holds an inspector edit before pushing it upstream
 * (TV1-2291 · t17). Milliseconds of pointer/keyboard idle.
 *
 * ⚠️ **THE INSPECTOR EMITS ON EVERY KEYSTROKE — ON PURPOSE (t16) — AND
 * `onChange` IS NOT A CHEAP CALL.** It lands in
 * `WidgetLayoutComponent.handleLayoutChange`, which resolves the layout, runs the
 * caller's validators, crosses the domain grammar seam and validates the result
 * against the SAME ajv schema the aggregate enforces. Two consequences make a
 * straight-through wiring untenable:
 *
 *  1. **A rejected update is a SWALLOWED KEYSTROKE.** `handleLayoutChange`
 *     returns before `setLayoutState` when validation fails, so `layout` never
 *     changes — and `layout` is where the inspector's field values come from.
 *     Half-typed input that momentarily fails the schema would therefore be
 *     un-typeable: the character appears and is instantly reverted, with an error
 *     toast per character.
 *  2. **Every accepted update re-fetches EVERY widget.** The Cube effect keys on
 *     `layoutState`, and `setLayoutState( resolved )` always produces a new
 *     object — so per-keystroke commits cost `N` round trips per character.
 *
 * The buffer below fixes (1) structurally (the overlay is what renders, so typing
 * always lands) and (2) by debouncing. 500ms is the same idle window the app's
 * subscription policies use; it is long enough to collapse a burst of typing and
 * short enough that a measure change repaints its data almost immediately.
 */
const INSPECTOR_COMMIT_DEBOUNCE_MS = 500;

/** A resolved widget carrying the identity `placement.ts` addresses it by. */
type PlacementWidget = ResolvedWidget & { i: string };

/** One frame of a gesture, in cells. Always the AUTHORED span — never the displayed one. */
type MovingRect = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PlacementOutcome = {
  /** The AUTHORED layout that would be persisted if the user let go now. */
  widgets: ResolvedWidget[];
  /** The card the moving card would trade places with, or `null` for push-and-compact. */
  swapTargetId: string | null;
};

/**
 * `compact` the placement `items`, then hand the result back in the order of the
 * `authored` array it came from.
 *
 * ⚠️ **`compact` RETURNS PLACEMENT ORDER, NOT INPUT ORDER** (top-to-bottom, then
 * left-to-right), and the widgets array is the report's READING order as well as
 * the auto-grid's flow order. Taking the compactor's array as-is would therefore
 * silently re-order the report every time a card changed row — which is why the
 * mapping back is by `definition.id` and never by index, and why it lives in one
 * place used by every mutation rather than being re-derived per call site.
 *
 * A widget the compactor did not move is returned BY REFERENCE, so `pushChange`
 * receives the same objects for the untouched cards.
 */
const compactInInputOrder = (
  authored: readonly ResolvedWidget[],
  items: readonly PlacementWidget[],
  priorityId: string | null,
  /** @see the `gravity` option on `compact` — off for anything the user placed. */
  gravity: boolean
): ResolvedWidget[] => {
  const placedById = new Map(
    compact( items, priorityId, { gravity }).map(( item ) => [ item.i, item ])
  );

  return authored.map(( widget ): ResolvedWidget => {
    const placed = placedById.get( widget.definition.id );
    if ( !placed ) return widget;
    if (
      placed.x === widget.x && placed.y === widget.y &&
      placed.width === widget.width && placed.height === widget.height
    ) {
      return widget;
    }
    return { ...widget, x: placed.x, y: placed.y, width: placed.width, height: placed.height };
  });
};

/**
 * Gravity-pack an AUTHORED layout — the non-gesture entry point to placement
 * (TV1-2291 · t32).
 *
 * ADD and REMOVE are MUTATIONS, so they go through the placement engine exactly
 * as drag and resize do. They differ from a gesture only in having no moving
 * rect and no swap: nothing is being aimed anywhere, so there is no priority
 * widget and `null` is the honest argument for it.
 *
 * ⚠️ **AUTHORED, never DISPLAY** — same reason as `resolvePlacement`: whatever
 * this returns is what `pushChange` persists, and compacting the minimum-clamped
 * array would write every untouched card's CLAMPED width back as its authored
 * one (INV-5, ticket 6's data-loss guard).
 *
 * ⚠️ **MUTATION PATH ONLY.** Do not lift this into a render `useMemo`: compacting
 * on render would re-pack a never-edited LLM-generated layout the moment it was
 * looked at, which is both a write-on-read and a rewrite of the author's flow
 * order (ticket 10 kept compaction off the idle path for exactly this).
 */
export const compactAuthored = (
  authored: readonly ResolvedWidget[],
  /**
   * ADD passes `true`, REMOVE passes `false` (2026-08-17).
   *
   * A newly added card has no position anyone chose, so floating it into the first
   * hole that fits is help. A REMOVE must not move the survivors: the cards left
   * behind are all at rows their author picked, and re-flowing them to close the
   * gap is the same "your placement is a suggestion" behaviour that made it
   * impossible to leave whitespace anywhere. Deleting one card should not
   * rearrange the report.
   */
  gravity: boolean
): ResolvedWidget[] =>
  compactInInputOrder(
    authored,
    authored.map(( w ): PlacementWidget => ({ ...w, i: w.definition.id })),
    null,
    gravity
  );

/**
 * Resolve one frame of a drag/resize into the layout it would produce
 * (TV1-2291 · SDD §2, ticket 10).
 *
 * ⚠️ IT OPERATES ON THE **AUTHORED** RESOLUTION, and that is not a detail.
 * Placement is a MUTATION — whatever it returns is what `pushChange` persists —
 * and the whole point of ticket 6's two-resolution split is that a mutation never
 * reads the minimum-clamped array. Feeding it `displayWidgets` would write every
 * untouched card's CLAMPED width back as its authored width the moment anything
 * is dragged on a narrow canvas: exactly the silent data loss INV-5 exists to
 * catch. The live preview is then obtained by resolving THIS output for DISPLAY
 * (`resolveLayout( previewAuthored, { colPx, scale })`), not the other way round.
 *
 * Three things the modules deliberately do not do, which therefore belong here:
 *
 * 1. **`swapCandidate` does not clamp `x` into the grid** — it is written to be
 *    grid-width-agnostic, so the caller owns `x = min( cols - width, x )`. The
 *    prototype does this in the swap branch of `pointermove`; without it a swap
 *    into a card near the right edge can hang the wider card off the canvas.
 * 2. **`compact` returns PLACEMENT order, not input order.** The widgets array's
 *    order is the report's reading order AND the auto-grid's flow order, so it is
 *    re-keyed by `definition.id` and reassembled in the INPUT order below. Mapping
 *    by index would silently rewrite the report every time a card changed row.
 * 3. **Hysteresis needs `armedId` to survive the gesture** — it is threaded in as
 *    an argument and returned as `swapTargetId` so the caller can hold it in a
 *    ref across pointer moves. A fresh `null` each frame collapses the arm (0.50)
 *    and disarm (0.34) gates into one and the preview strobes on the boundary.
 *
 * Pure: nothing is mutated, and a widget the placement did not touch is returned
 * by reference.
 */
export const resolvePlacement = (
  authored: readonly ResolvedWidget[],
  moving: MovingRect,
  cols: number,
  armedId: string | null,
  { allowSwap }: { allowSwap: boolean }
): PlacementOutcome => {
  const items: PlacementWidget[] = authored.map(( w ) => ({ ...w, i: w.definition.id }));
  const origin = items.find(( item ) => item.i === moving.id );
  // The moving widget is not in the layout (a stale gesture against a layout that
  // changed underneath). Placing it would ADD a phantom card; do nothing instead.
  if ( !origin ) return { widgets: [...authored], swapTargetId: null };

  const nextRect: PlacementRect = {
    i: moving.id,
    x: moving.x,
    y: Math.max( 0, moving.y ),
    width: moving.width,
    height: moving.height
  };

  // See (1) above. `Math.max( 0, cols - width )` rather than `cols - width` so a
  // widget wider than the whole grid clamps to 0 instead of a negative column.
  const clampX = ( x: number, width: number ): number =>
    Math.min( Math.max( 0, x ), Math.max( 0, cols - width ));

  // Resize never swaps — the prototype passes `{ target: null }` for it, and it is
  // right to: a swap is a statement about where a card should GO, and a resize is
  // not moving the card.
  const target = allowSwap ? swapCandidate( items, nextRect, armedId ).target : null;

  const proposed = items.map(( item ): PlacementWidget => {
    if ( item.i === moving.id ) {
      const landing = target ? { x: target.x, y: target.y } : { x: nextRect.x, y: nextRect.y };
      return {
        ...item,
        x: clampX( landing.x, nextRect.width ),
        y: landing.y,
        width: nextRect.width,
        height: nextRect.height
      };
    }
    // The swapped-out card takes the moving card's DRAG ORIGIN, not its current
    // position — `origin` is read off the pre-gesture authored array.
    if ( target && item.i === target.i ) {
      return { ...item, x: clampX( origin.x, item.width ), y: origin.y };
    }
    return item;
  });

  // `moving.id` is the priority: at equal `y` the dragged card takes the row and
  // the incumbent is pushed down, so the user's explicit intent beats an accident
  // of column order. See (2) above — reassembled in INPUT order, keyed by id.
  // GRAVITY OFF. A drag/resize is the user stating a position, and the float-up
  // used to overrule it — drop a card at row 8 of an empty report and it snapped
  // to row 0. Overlaps are still resolved by pushing down; only the "pull
  // everything to the top" pass is gone. See `compact`'s `gravity` option.
  const widgets = compactInInputOrder( authored, proposed, moving.id, false );

  return { widgets, swapTargetId: target ? target.i : null };
};

type WidgetLayoutEditorProps = {
  layout: WidgetLayout;
  onChange: ( layout: WidgetLayout ) => void;
  availableWidgets: WidgetLayout['widgets'][number]['definition']['type'][];
  widgetDataMap: Record<string, WidgetDataState>;
  scale?: number;
  /** Shared Cube client threaded down so the builder can read the live `/meta` schema. */
  cubeApi?: CubeApi;
};

export default function WidgetLayoutEditor({ layout, widgetDataMap, onChange, availableWidgets, scale = 1, cubeApi }: WidgetLayoutEditorProps ) {
  //state
  const { t } = useTranslation( 'widgets' );
  /*
   * The live `/meta` catalog, fetched ONCE for the whole editor (2026-08-17).
   *
   * It was fetched inside `WidgetInspector`, which needs it for the builder
   * sections. Two things changed that: the panel is no longer always mounted (so
   * the catalog would arrive and be thrown away every time it closed), and
   * `addWidgetOfType` now needs it too, to build an analytics preset against a
   * measure that actually exists. Owning it here means one round trip per editor
   * session instead of one per panel opening, and the panel receives it as props.
   */
  const {
    catalog: cubeCatalog,
    loading: cubeMetaLoading,
    error: cubeMetaError
  } = useCubeMeta( cubeApi );
  /**
   * TV1-2291 (t17) — THE SELECTION, which replaces `editingWidget`/`creatingWidget`.
   *
   * The inspector is DOCKED, so "which card am I configuring" is now a property of
   * the editor rather than of a modal that is open or shut. Held as an ID and never
   * as a widget object: every mutation rebuilds the widgets array, so a captured
   * object would go stale the first time the card moved, and the ID is also what
   * `WidgetCard`'s `selected` prop and the authored lookup both key on (INV-5 — the
   * two resolutions are matched by `definition.id`, never by index).
   */
  const [ selectedWidgetId, setSelectedWidgetId ] = useState<string | null>( null );
  /**
   * Report settings (name, description) opened DELIBERATELY, with nothing selected.
   *
   * The panel is no longer docked-always (2026-08-17), so "the report's own
   * settings" lost the permanent home it used to have as the panel's default pane.
   * Rather than bring the permanent column back for two text fields, they keep the
   * panel but need an explicit way in — this flag is it, raised by the ⚙ control
   * over the canvas and cleared by the panel's own close button.
   */
  const [ reportPaneOpen, setReportPaneOpen ] = useState( false );
    // is Editing any wdiget -> show grid, etc.
  const [ isInteracting, setIsInteracting ] = useState( false );
    // delete confirmation modal state
  const [ deleteModalOpen, setDeleteModalOpen ] = useState( false );
  const [ widgetToDelete, setWidgetToDelete ] = useState<Widget | null>( null );

  // ── TV1-2291 (t17) — THE INSPECTOR EDIT BUFFER ───────────────────────────
  //
  // See `INSPECTOR_COMMIT_DEBOUNCE_MS` for WHY this exists. Mechanically:
  //
  //   `bufferedLayout` is an OVERLAY. While it is set, it — not the `layout`
  //   prop — is what the canvas, the authored array and the inspector all read,
  //   so an edit is visible on the keystroke whether or not it has been accepted
  //   upstream yet. The commit is debounced behind it.
  //
  // It is deliberately a WHOLE layout rather than "the definition being edited":
  // the report's own name/description are edited in the same panel, per keystroke,
  // through the same `handleLayoutChange`, and they have exactly the same problem.
  const [ bufferedLayout, setBufferedLayout ] = useState<WidgetLayout | null>( null );
  const bufferedCommitRef = useRef<WidgetLayout | null>( null );
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>( null );
  // Read by the debounce timer, which fires outside the render that scheduled it.
  const onChangeRef = useRef( onChange );
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /*
   * The overlay is dropped whenever the host hands down a new `layout`, and that
   * single line is what makes the buffer safe in both directions:
   *
   *   ACCEPTED commit  -> `handleLayoutChange` calls `setLayoutState( resolved )`,
   *                       which always produces a NEW object, so `layout`'s
   *                       identity changes and the (now redundant) overlay goes.
   *   REJECTED commit  -> it returns BEFORE `setLayoutState`, so `layout` does not
   *                       change and the overlay survives — carrying the user's
   *                       half-typed text, which is the whole point.
   *
   * `layout` is the parent's `layoutState`, so its identity does not churn on
   * unrelated re-renders; it changes on a commit or on a new report, and dropping
   * an in-flight overlay is the right answer for both.
   */
  useEffect(() => {
    setBufferedLayout( null );
  }, [layout]);

  const cancelScheduledCommit = useCallback(() => {
    if ( commitTimerRef.current === null ) return;
    clearTimeout( commitTimerRef.current );
    commitTimerRef.current = null;
    bufferedCommitRef.current = null;
  }, []);

  const scheduleCommit = useCallback(( next: WidgetLayout ) => {
    bufferedCommitRef.current = next;
    if ( commitTimerRef.current !== null ) clearTimeout( commitTimerRef.current );
    commitTimerRef.current = setTimeout(() => {
      commitTimerRef.current = null;
      const pending = bufferedCommitRef.current;
      bufferedCommitRef.current = null;
      if ( pending ) onChangeRef.current( pending );
    }, INSPECTOR_COMMIT_DEBOUNCE_MS );
  }, []);

  // FLUSH ON UNMOUNT. `WidgetLayoutComponent` swaps the editor for the viewer when
  // edit mode is turned off, so the editor routinely unmounts while its host lives
  // on — and the last few hundred milliseconds of typing must not be discarded
  // just because the user reached for the toggle straight after typing.
  useEffect(() => () => {
    if ( commitTimerRef.current === null ) return;
    clearTimeout( commitTimerRef.current );
    commitTimerRef.current = null;
    const pending = bufferedCommitRef.current;
    bufferedCommitRef.current = null;
    if ( pending ) onChangeRef.current( pending );
  }, []);

  /** The layout everything below reads: the buffered overlay, or the prop. */
  const effectiveLayout = bufferedLayout ?? layout;

  // TV1-2291 (S1/S2) — one definition of the pixel math, shared with the viewer,
  // over the width the editor is actually GIVEN rather than a hard-coded 827px.
  // The scroller is measured, not the canvas: the canvas carries an explicit
  // pixel width, so observing it would feed the ResizeObserver its own output.
  //
  // Measured off `layout` rather than `effectiveLayout` on purpose: the buffer
  // only ever carries definition / name / description edits, never `cols`,
  // `widthPx` or `device`, so recomputing the geometry (and everything memoised
  // on it) once per keystroke would be pure cost.
  //
  // ⚠️ TWO MEASUREMENTS, AND THEY ARE NOT INTERCHANGEABLE (2026-08-17).
  //
  //   editorWidth    the whole editor ROW. Does NOT change when the panel mounts.
  //                  Drives `canEdit` ONLY.
  //   containerWidth the canvas scroller. DOES change when the panel mounts.
  //                  Drives the grid geometry ONLY.
  //
  // The split is what lets the inspector be CONDITIONAL. While it was mounted
  // unconditionally, one measurement was enough and a fixed 348px placeholder held
  // the column open whether or not the panel was in it — the `.inspectorPlaceholder`
  // this rework deletes. Mount the panel conditionally against that single
  // measurement and the two feed each other:
  //
  //     panel mounts -> scroller narrows -> width < EDIT_MIN_CANVAS_PX
  //       -> read-only -> panel unmounts -> scroller widens -> editable -> …
  //
  // …at about one flip per frame, for every canvas between EDIT_MIN_CANVAS_PX and
  // EDIT_MIN_CANVAS_PX + 348. Reserving the width was one way to break that loop;
  // measuring something the panel cannot affect is the better one, because it also
  // gives the canvas the full row back when the panel is closed — which is the
  // whole point of closing it.
  const [ editorRef, editorWidth ] = useContainerWidth<HTMLDivElement>();
  const [ scrollerRef, containerWidth ] = useContainerWidth<HTMLDivElement>();
  const geometry = useMemo(
    () => computeGridGeometry( layout, { scale, containerWidthPx: containerWidth }),
    [ layout, scale, containerWidth ]
  );
  const { colPx, rowPx, device } = geometry;

  // TV1-1931 — positionless widgets (an LLM-generated layout) get their x/y/w/h
  // from the auto-grid HERE, at render time. Storage stays positionless; this
  // must NOT be pushed back into `layoutState` (the Cube fetch effect in
  // `WidgetLayoutComponent` keys on it and would re-fetch on every resolve).
  // Every mutation below reads a RESOLVED layout, so the first edit persists
  // concrete coordinates for the whole layout and no mixed state survives.
  //
  // ⚠️ TV1-2291 (S6) — THE DATA-LOSS GUARD. THE EDITOR HOLDS TWO RESOLUTIONS OF
  // THE SAME LAYOUT AND THEY ARE NOT INTERCHANGEABLE.
  //
  // Every mutation below rebuilds the whole layout from a resolved array and
  // persists ALL of it. So if that array were the minimum-CLAMPED one, dragging
  // ONE card on a narrow viewport would write back EVERY OTHER card's clamped
  // width as its authored width — the desktop arrangement destroyed by a drag
  // that never touched those widgets, silently and with no error.
  //
  //   AUTHORED — no geometry, no clamping. The MUTATION BASE. Never rendered.
  //   DISPLAY  — clamped to the legible minimums at the current width.
  //              RENDERED. Never persisted.
  //
  // The two arrays hold the same widgets in the same order (clamping changes
  // `width`/`x`/`y`, never membership or order) — but clamping CAN move a
  // *flowed* widget's `y`, because a widened widget wraps earlier. So every
  // lookup across the two goes by `definition.id`, never by array index.
  //
  // Both resolve `effectiveLayout`, i.e. the buffered overlay when there is one —
  // that is what makes an inspector edit visible on the canvas immediately, and
  // what lets every mutation below carry the buffered edit along with it for free.
  const authoredLayout = useMemo(() => resolveLayout( effectiveLayout ), [effectiveLayout]);
  const authoredWidgets = authoredLayout.widgets;
  const displayWidgets = useMemo(
    // `scale` is passed separately from `colPx` on purpose: `colPx` already
    // carries the zoom and the minimum must be measured against the 1x width,
    // or zooming out would re-clamp and the report would reflow mid-gesture.
    () => resolveLayout( effectiveLayout, { colPx: geometry.colPx, scale, gapPx: geometry.gapPx }).widgets,
    [ effectiveLayout, geometry.colPx, scale ]
  );

  // Gate editing on the MEASURED canvas only, and FAIL OPEN when there is no
  // measurement yet.
  //
  // ⚠️ `geometry.canvasWidthPx` is NOT usable here. When `containerWidth` is 0 —
  // first paint before the ResizeObserver fires, a hidden tab, a collapsed
  // parent — `computeGridGeometry` falls back to the layout's own `widthPx`,
  // which is 827 by default on EVERY existing report. Gating on that made the
  // editor declare itself read-only on every mount, and permanently so wherever
  // the observer never fired. Since `canEdit` also hides the palette, the resize
  // handles, card selection and the inspector, that one boolean made the entire
  // feature set unreachable.
  //
  // So: an unmeasured canvas means "not known", never "too small". The
  // authored/display split (SDD K3) is what protects the data on a narrow drag;
  // this is only a usability decline for a phone, and a usability decline must
  // never be the thing that switches the editor off.
  //
  // `editorWidth` is the raw measurement, so it carries no zoom — zoom must not
  // switch editing on or off, for the same reason it must not re-clamp. It is the
  // ROW, not the scroller: see the two-measurement note above for why gating on
  // the scroller cannot survive a conditional panel.
  const canEdit = editorWidth <= 0 || editorWidth >= EDIT_MIN_CANVAS_PX;

  // TV1-2291 (t10) — THE LIVE PLACEMENT PREVIEW.
  //
  // `widgets` is the AUTHORED layout that would be persisted if the pointer were
  // released now: siblings already pushed and compacted, the swap (if armed)
  // already applied. It is recomputed on every pointer move and thrown away on
  // release — the commit re-derives it from the drop coordinates, which are the
  // authoritative ones.
  const [ preview, setPreview ] = useState<( PlacementOutcome & { activeId: string }) | null>( null );
  //
  // ⚠️ THE ARMED SWAP TARGET LIVES IN A REF, ON PURPOSE. Hysteresis is stateful:
  // `swapCandidate` gates at 0.50 for a NEW target and at 0.34 for the one already
  // armed, and it learns which is which only from the `armedId` it is handed. Pass
  // a fresh `null` on each pointer move and the two gates collapse into one — the
  // preview then flips between "swap" and "push" on every stray pixel while the
  // pointer rests near the boundary, which is exactly where a user hovers while
  // deciding. A ref (not state) because it must be readable and writable WITHIN a
  // single move handler, before React has re-rendered anything.
  const swapArmedIdRef = useRef<string | null>( null );

  // The DISPLAY resolution OF THE PREVIEWED AUTHORED LAYOUT. Same direction as
  // the non-gesture path — authored is what we mutate, display is what we draw —
  // so the clamping rules are identical mid-drag and at rest.
  const previewDisplay = useMemo(() => {
    if ( !preview ) return null;
    const resolved = resolveLayout(
      { ...effectiveLayout, widgets: preview.widgets },
      { colPx: geometry.colPx, scale, gapPx: geometry.gapPx }
    ).widgets;
    return new Map( resolved.map(( w ) => [ w.definition.id, w ]));
  }, [ preview, effectiveLayout, geometry.colPx, scale ]);

  /**
   * Where the dragged card will LAND, in px — the ghost's rect.
   *
   * Read off the previewed DISPLAY resolution of the ACTIVE card, which is the
   * post-swap, post-compaction cell. Deliberately NOT the pointer position: on a
   * grid that pushes, "where the cursor is" and "where the card ends up" are
   * routinely different rows, and a ghost that tracked the cursor would promise
   * the wrong one.
   */
  const ghostRect = useMemo(() => {
    if ( !preview || !previewDisplay ) return null;
    const landing = previewDisplay.get( preview.activeId );
    if ( !landing ) return null;
    const { left, top, width, height } = widgetRectPx( landing, geometry );
    return { transform: `translate(${ left }px, ${ top }px)`, width, height };
  }, [ preview, previewDisplay, geometry ]);

  /** What the DOM should draw for `w` this frame — the preview rect, or `w` itself. */
  const renderRectOf = ( w: ResolvedWidget ): ResolvedWidget => {
    // react-rnd owns the DRAGGED card for the duration of the gesture: it is
    // already translating/resizing the node itself, and feeding it a new
    // `position`/`size` mid-gesture makes the card fight the pointer. Siblings
    // are plain controlled `<Rnd>`s and take their previewed cells.
    if ( !previewDisplay || preview?.activeId === w.definition.id ) return w;
    return previewDisplay.get( w.definition.id ) ?? w;
  };

  // 2 rows of slack, not the viewer's 1 — the editor needs somewhere to drop a
  // widget at the bottom of the canvas. Measured off the DISPLAY resolution:
  // this sizes the DOM.
  const canvasHeight = useMemo(
    () => canvasHeightPx( displayWidgets, geometry, 2 ),
    [ displayWidgets, geometry ]
  );

  //TODO: deberia pasarle el cambio tambien??
  //
  // TV1-2291 (t17) — the IMMEDIATE write path. Every mutation below is built by
  // spreading `authoredLayout`, which already carries whatever the inspector has
  // buffered — so a scheduled commit would push the very same edit a second time
  // (and cost another N Cube round trips). Cancelling it here is what keeps that
  // invariant true for every call site at once, present and future.
  const pushChange = useCallback(( next: WidgetLayout ) => {
    cancelScheduledCommit();
    onChange( next );
    }, [ onChange, cancelScheduledCommit ]);

  /**
   * The BUFFERED write path — the inspector's, and only the inspector's.
   *
   * Overlay now (so the canvas repaints on the keystroke), commit on idle. See
   * `INSPECTOR_COMMIT_DEBOUNCE_MS` for why a straight `pushChange` here would make
   * the panel's own fields un-typeable.
   */
  const bufferChange = useCallback(( next: WidgetLayout ) => {
    setBufferedLayout( next );
    scheduleCommit( next );
  }, [scheduleCommit]);

  // update — reads and writes AUTHORED (see the two-resolution note above). The
  // `patch` carries CELL coordinates that were converted from pointer pixels
  // against the DISPLAY geometry; cells are the common currency of the two
  // resolutions, so they apply to the authored widget unchanged.
  const updateWidget = ( id: string, patch: Partial<Widget> ) =>
    pushChange({
      ...authoredLayout,
      widgets: authoredWidgets.map(( w ) => w.definition.id === id ? { ...w, ...patch } : w )
    });

  // --- Drag / resize: placement (TV1-2291 · t10) --------------------------
  //
  // Replaces the old "write the dragged card's new cell and hope nothing was
  // already there". It could not fail safely: two widgets touching was REFUSED by
  // `noOverlapValidator`, so a drop one pixel into a neighbour was a dead end —
  // an error toast and no save. Compaction RESOLVES it instead, which is why the
  // validator is retired in this same slice (concern C1): a world with neither
  // permits overlap silently.

  /**
   * One frame of a gesture. Returns the AUTHORED layout it would produce, and
   * carries the armed swap target forward in `swapArmedIdRef` so the hysteresis
   * gates mean something across moves.
   */
  const runPlacement = ( id: string, moving: Omit<MovingRect, 'id'>, allowSwap: boolean ): PlacementOutcome => {
    const outcome = resolvePlacement(
      authoredWidgets,
      { id, ...moving },
      geometry.cols,
      swapArmedIdRef.current,
      { allowSwap }
    );
    swapArmedIdRef.current = outcome.swapTargetId;
    return outcome;
  };

  const beginGesture = () => {
    setIsInteracting( true );
    // A gesture starts unarmed. Leaving the previous gesture's target armed would
    // let a brand-new drag swap at the DISARM ratio (0.34) on its first frame.
    swapArmedIdRef.current = null;
  };

  const endGesture = () => {
    setIsInteracting( false );
    setPreview( null );
    swapArmedIdRef.current = null;
  };

  const previewGesture = ( id: string, moving: Omit<MovingRect, 'id'>, allowSwap: boolean ) => {
    const outcome = runPlacement( id, moving, allowSwap );
    // The canvas has to hold what the PREVIEW says, not just where the pointer
    // is: a push sends siblings DOWN, routinely past the bottom of a canvas
    // sized for the pre-drag layout, and a card the user cannot see has been
    // moved without them being shown it. Rows are identical in both resolutions
    // here (every previewed widget is pinned, and clamping only ever changes a
    // FLOWED widget's row), so the authored bottom is the displayed bottom.
    ensureCanvasHeight(
      outcome.widgets.reduce(( bottom, w ) => Math.max( bottom, w.y + w.height ), 0 ) * rowPx
    );
    setPreview({ ...outcome, activeId: id });
  };

  const commitGesture = ( id: string, moving: Omit<MovingRect, 'id'>, allowSwap: boolean ) => {
    // Recomputed from the DROP coordinates rather than reusing the last preview:
    // the preview is a frame of a moving pointer, this is where it actually
    // stopped. `swapArmedIdRef` still holds the armed target, so the verdict is
    // the same one the user was being shown.
    const outcome = runPlacement( id, moving, allowSwap );
    pushChange({ ...authoredLayout, widgets: outcome.widgets });
    endGesture();
  };

  /** The AUTHORED span of `id` — what placement moves, whatever the DOM drew. */
  const authoredSpanOf = ( id: string ): { width: number; height: number } => {
    const authored = authoredWidgets.find(( w ) => w.definition.id === id );
    return { width: authored?.width ?? 1, height: authored?.height ?? 1 };
  };

  //delete
  const handleDeleteClick = ( widget: Widget ) => {
    setWidgetToDelete( widget );
    setDeleteModalOpen( true );
  };

  const confirmDelete = () => {
    if ( widgetToDelete ) {
      // `widgetToDelete` came off the DISPLAY array (it is what the DOM renders),
      // so the filter matches on `definition.id` rather than on the object.
      //
      // TV1-2291 (t32) — AND THEN COMPACT. Removing a card used to push the
      // remainder straight through, so the cells it occupied stayed empty
      // FOREVER: nothing else in the editor closes a hole, and the user's only
      // ⚠️ REVERSED 2026-08-17 — remove NO LONGER re-flows the survivors.
      //
      // t32 made removal gravity-pack the whole report, on the reasoning that a
      // drop already moves cards the user never touched. Both halves of that turned
      // out to be the same defect: dragging did not respect placement either, and
      // once it does (see `resolvePlacement`), "deleting one card rearranges the
      // report" is just the last place the engine overrules the author. Removing a
      // card now leaves exactly the hole it occupied, and the author closes it by
      // dragging if they want it closed.
      pushChange({
        ...authoredLayout,
        widgets: compactAuthored(
          authoredWidgets.filter(( w ) => w.definition.id !== widgetToDelete.definition.id ),
          false
        )
      });
      // The card being configured has just ceased to exist, so the panel closes.
      // Clearing the selection is the whole of it now — the pane is derived from
      // it, so there is no second piece of state left to drift out of step.
      if ( widgetToDelete.definition?.id === selectedWidgetId ) {
        setSelectedWidgetId( null );
      }
      setDeleteModalOpen( false );
      setWidgetToDelete( null );
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen( false );
    setWidgetToDelete( null );
  };

  // ── TV1-2291 (t17) — SELECTION + THE INSPECTOR'S WRITE PATHS ─────────────

  /**
   * Select a card. The ✎ affordance and a plain click share it.
   *
   * Selecting IS opening the panel now — there is no separate pane to switch to,
   * because `inspectorPane` is derived from the selection below rather than held.
   * Two pieces of state that must agree ("selected id" and "which pane") is how
   * `selection + layout pane` and `no selection + card pane` both became reachable
   * under S16; one derived value cannot disagree with itself.
   */
  const selectWidget = ( widgetId: string | undefined ) => {
    if ( !widgetId ) return;
    setSelectedWidgetId( widgetId );
    // A card takes the panel over from report settings; leaving the flag raised
    // would spring the report pane back the moment the card is deselected.
    setReportPaneOpen( false );
  };

  /**
   * Clicking the report BACKGROUND closes the panel (2026-08-19).
   *
   * Ticket owner: *"it should close when clicking on the report background"*. With
   * the panel docked in flow there is no overlay to click away, so the canvas has to
   * offer the gesture itself — and it is the one every editor has: click the
   * artboard, nothing is selected.
   *
   * ⚠️ THE GUARD IS THE WHOLE THING. This sits on the scroller, so every click
   * inside it bubbles here — including the click that SELECTED a card, which would
   * otherwise deselect it again in the same event and make cards unselectable.
   * `closest( '[data-widget-frame]' )` is what separates "the background" from "a
   * card": `<Rnd>` spreads that attribute onto its root, so it covers the card, its
   * action bar and its resize grips in one test.
   *
   * Deliberately NOT `event.target === event.currentTarget`: the canvas is a child
   * of the scroller, so a click on the canvas's empty area has the canvas as its
   * target and would fail that test — which is precisely the click most likely to
   * mean "deselect".
   */
  const handleBackgroundClick = ( event: React.MouseEvent<HTMLDivElement> ) => {
    if ( !canEdit ) return;
    if (( event.target as HTMLElement ).closest( '[data-widget-frame]' )) return;
    closeInspector();
  };

  /** Dismiss the panel — the only control it has. Deselecting is what closes it. */
  const closeInspector = () => {
    setSelectedWidgetId( null );
    setReportPaneOpen( false );
  };

  /**
   * WHICH PANE, and WHETHER THE PANEL EXISTS AT ALL.
   *
   * Both derived, never stored. The panel is mounted when there is something to
   * edit — a selected card, or report settings asked for on purpose — and not
   * otherwise. That is the ticket owner's call of 2026-08-17 ("the side one for
   * edition and content editing, and not everytime"), and it is also what lets the
   * canvas have the whole row back when nothing is being configured.
   */
  const inspectorPane: WidgetInspectorPane = selectedWidgetId ? 'card' : 'layout';
  const inspectorOpen = canEdit && ( selectedWidgetId !== null || reportPaneOpen );

  /**
   * A click on a card SWITCHES the open panel; it never OPENS one (2026-08-19).
   *
   * ⚠️ REVERSED FROM THE SAME DAY'S FIRST ATTEMPT, on the ticket owner's
   * correction: *"dont edit on click, only when clicking the edit button. then, when
   * the panel is already opened, clicking on other widgets open their corresponding
   * panel. once you close the panel, clicking on them wont open the panel."*
   *
   * So the card click is MODAL on whether the panel is already up:
   *
   *   panel closed -> a click selects nothing. The canvas is for arranging: you
   *                   click a card to drag it, and having that open a 420px panel
   *                   that steals half the canvas is a surprise, not a shortcut.
   *   panel open   -> a click moves the panel to that card. Once you are in
   *                   configure mode, walking between cards should not cost a trip
   *                   to each one's ✎ button.
   *
   * The ✎ action is the ONLY thing that opens it, in both states — it is the
   * control that says "edit this", and it goes on calling `selectWidget` directly.
   *
   * `inspectorOpen` rather than `selectedWidgetId`: with the REPORT pane up (⚙, no
   * card selected) the panel is still open, and clicking a card should take it over
   * — the user is already configuring, which is the whole condition.
   */
  const handleCardClick = ( widgetId: string | undefined ) => {
    if ( !inspectorOpen ) return;
    selectWidget( widgetId );
  };

  /**
   * The selected card, looked up in the AUTHORED array by `definition.id`.
   *
   * ⚠️ AUTHORED, never `displayWidgets` (INV-5). The inspector's edits are
   * MUTATIONS, so they compose with an array whose widths are the author's rather
   * than the current viewport's; reading the clamped one here would hand every
   * subsequent write the displayed width as if it had been authored.
   */
  const selectedWidget = useMemo(
    () => authoredWidgets.find(( w ) => w.definition?.id === selectedWidgetId ) ?? null,
    [ authoredWidgets, selectedWidgetId ]
  );

  /** A definition edit — buffered, because the panel emits one per keystroke. */
  const handleInspectorWidgetChange = ( definition: WidgetDefinition ) => {
    bufferChange({
      ...authoredLayout,
      widgets: authoredWidgets.map(( w ) => (
        w.definition?.id === definition.id ? { ...w, definition } : w
      ))
    });
  };

  /** Report-level edits (name, description) — per keystroke too, same buffer. */
  const handleInspectorLayoutChange = ( next: WidgetLayout ) => {
    bufferChange({ ...next, widgets: authoredWidgets });
  };

  /**
   * The card tint.
   *
   * ⚠️ Written to the layout WRAPPER (`w.tint`), NEVER to `w.definition` (t13/t20).
   * `widgetGrammar.modelVariant()` derives the LLM's structured-output schema by
   * omitting exactly `id` and `widgetComponentProps` from the definition, so ANY
   * other top-level definition field is inherited into it — a per-definition tint
   * would hand a language model a card-presentation key it must never author.
   *
   * Committed immediately rather than buffered: a swatch is one discrete click,
   * not a keystroke stream, so there is no burst to collapse.
   */
  const handleTintChange = ( tint: WidgetTint ) => {
    if ( !selectedWidgetId ) return;
    pushChange({
      ...authoredLayout,
      widgets: authoredWidgets.map(( w ) => (
        w.definition?.id === selectedWidgetId ? { ...w, tint } : w
      ))
    });
  };

  /**
   * The author's TEXT colour, written to the same WRAPPER `tint` rides on
   * (2026-08-18). Not into the definition, for the reason spelled out on
   * `WidgetInspectorProps.tint`: the definition is the LLM output gate and
   * `widgetGrammar.modelVariant()` inherits every top-level field it carries, so a
   * card-presentation key placed there would be handed to a language model.
   *
   * `undefined` CLEARS it — that is how "go back to the theme default" is spelled,
   * and it has to be distinguishable from a chosen colour that happens to match the
   * default, because only the former follows a theme change.
   */
  const handleTextColorChange = ( textColor: string | undefined ) => {
    if ( !selectedWidgetId ) return;
    pushChange({
      ...authoredLayout,
      widgets: authoredWidgets.map(( w ) => (
        w.definition?.id === selectedWidgetId ? { ...w, textColor } : w
      ))
    });
  };

  /** Remove, from the panel — through the SAME confirmation modal as the card's ✕. */
  const handleInspectorRemove = ( widgetId: string ) => {
    const target = authoredWidgets.find(( w ) => w.definition?.id === widgetId );
    if ( target ) handleDeleteClick( target );
  };

  //add
  const addWidget = ( widgetDefinition: WidgetDefinition ) => { //widget defintiion
    // The new widget is POSITIONLESS by design (TV1-1931 · `createWidget`) — the
    // auto-grid flows it in below the existing (already-resolved, hence pinned)
    // ones, and that resolve is what gives it an x/y/width/height at all.
    //
    // TV1-2291 (t32) — RESOLVE, THEN COMPACT. The flow deliberately starts below
    // EVERY pinned widget, so on its own it drops the card at the bottom of the
    // report even when there is room for it three rows up. Compaction is what
    // floats it into a hole it legitimately fits, exactly as a drop does.
    //
    // ⚠️ The resolve is the AUTHORED one — NO `{ colPx, scale }`. This is a WRITE
    // path (see the two-resolution note above): clamping here would persist every
    // other card's displayed width as its authored one.
    const widgetCreated = createWidget( widgetDefinition );
    // Resolve so the new card HAS a rect (it is positionless by construction), then
    // slot it into the first gap that fits — `placeWithoutMoving`, NOT a global
    // compaction (2026-08-17). Gravity-packing the whole report to make room would
    // drag every existing card up, including ones deliberately parked low; adding a
    // card must not rearrange the report around it. Nothing but the new card moves.
    const resolvedWithNew = resolveLayout({
      ...authoredLayout,
      widgets: [ ...authoredWidgets, widgetCreated ]
    }).widgets;

    const existing = resolvedWithNew
      .filter(( w ) => w.definition.id !== widgetCreated.definition.id )
      .map(( w ): PlacementWidget => ({ ...w, i: w.definition.id }));
    const incoming = resolvedWithNew.find(( w ) => w.definition.id === widgetCreated.definition.id );
    const placed = incoming
      ? placeWithoutMoving( existing, { ...incoming, i: incoming.definition.id })
      : null;

    pushChange({
      ...authoredLayout,
      widgets: resolvedWithNew.map(( w ) => (
        placed && w.definition.id === widgetCreated.definition.id ? { ...w, y: placed.y } : w
      ))
    });
    // TV1-2291 (t17) — AND SELECT IT. There is no builder modal to configure the
    // new card in any more: it lands on the canvas unconfigured (it will draw an
    // error tile until it has a data source), and the inspector's card pane is
    // where it becomes a real widget. Dropping a card the user then has to hunt
    // for and click would be a worse trade than the modal it replaces.
    selectWidget( widgetCreated.definition.id );
  };

  /**
   * Add a WORKING card of `type` (2026-08-17).
   *
   * ⚠️ It used to add a BLANK one — `{ name: '', cube: '', measures: [] }` — and
   * that is what made the palette undiscoverable. The card landed announcing three
   * missing fields, beside a form of seven collapsed sections, five of them locked
   * until you happened to fill in the right one first. For the readmodel cards
   * (`alert`, `count`, `list`, `progress`) it was worse than hard: their entire
   * configuration is a collection name that appears nowhere in the UI until a
   * collection is already chosen, so "make an alert" had no discoverable path at
   * all.
   *
   * `presetDefinitionFor` returns a complete, schema-valid card instead, so
   * configuring becomes ADJUSTING something that already renders rather than
   * assembling it from nothing — and a preset card is never "incomplete", so it
   * shows no missing-field notice and never trips the save gate. See that module
   * for why the analytics presets resolve against the live cube catalog rather
   * than hard-coding a measure name.
   */
  const addWidgetOfType = ( type: WidgetType ) =>
    addWidget( presetDefinitionFor( type, cubeCatalog, uuidv7()));

  //snapping
  const snapGrid: [number, number] = [ colPx, rowPx ];

  //autogrow
  const wrapperRef = useRef<HTMLDivElement>( null );
  const canvasRef = wrapperRef;
  const ensureCanvasHeight = useCallback(
      ( neededBottomPx: number ) => {
        const extra = rowPx * 2; // keep the +2-row buffer
        const target = neededBottomPx + extra;
        if ( wrapperRef.current && target > wrapperRef.current.clientHeight ) {
          wrapperRef.current.style.height = `${target}px`;
        }
      },
      [rowPx]
    );
  useLayoutEffect(() => {
    if ( wrapperRef.current ) {
      wrapperRef.current.style.height = `${canvasHeight}px`;
    }
  }, [canvasHeight]);

  // auto-scroll
  const SCROLL_EDGE_THRESHOLD = 60;
  const MAX_SCROLL_SPEED = 20;
  const scrollAnimationRef = useRef<number | null>( null );
  const scrollStateRef = useRef({ isScrolling: false, direction: 0, intensity: 0 });

  useLayoutEffect(() => {
    return () => {
      if ( scrollAnimationRef.current !== null ) {
        cancelAnimationFrame( scrollAnimationRef.current );
      }
    };
  }, []);

  const animateScroll = useCallback(() => {
    const canvas = canvasRef.current;
    if ( !canvas || !scrollStateRef.current.isScrolling ) return;
    
    canvas.scrollTop += scrollStateRef.current.direction * scrollStateRef.current.intensity;
    
    scrollAnimationRef.current = requestAnimationFrame( animateScroll );
  }, []);

  const handleAutoScroll = useCallback(( clientY: number ) => {
    const canvas = canvasRef.current;
    if ( !canvas ) return;

    const rect = canvas.getBoundingClientRect();
    const { top, bottom } = rect;
    const mouseOffsetTop = clientY - top;
    const mouseOffsetBottom = bottom - clientY;

    let direction = 0;
    let intensity = 0;
    
    if ( mouseOffsetBottom < SCROLL_EDGE_THRESHOLD ) {
      direction = 1;
      intensity = Math.min( 1, ( SCROLL_EDGE_THRESHOLD - mouseOffsetBottom ) / SCROLL_EDGE_THRESHOLD );
    } else if ( mouseOffsetTop < SCROLL_EDGE_THRESHOLD ) {
      direction = -1;
      intensity = Math.min( 1, ( SCROLL_EDGE_THRESHOLD - mouseOffsetTop ) / SCROLL_EDGE_THRESHOLD );
    }
    
    intensity = intensity * intensity * MAX_SCROLL_SPEED;
    
    const isScrolling = direction !== 0 && intensity > 0;
    scrollStateRef.current = { isScrolling, direction, intensity };
    
    if ( isScrolling && scrollAnimationRef.current === null ) {
      scrollAnimationRef.current = requestAnimationFrame( animateScroll );
    } else if ( !isScrolling && scrollAnimationRef.current !== null ) {
      cancelAnimationFrame( scrollAnimationRef.current );
      scrollAnimationRef.current = null;
    }
  }, [animateScroll]);
  
  const renderWidgetForDevice = ( device: string, widget: Widget ) => {
    const deviceComponents: Record<string, ( widget: Widget ) => JSX.Element> = {
      web: ( w ) => (
        <WebWidget
          widget={w}
          data={widgetDataMap[w.definition.id]}
          type={w.definition.type}
          widgetComponentProps={w.definition.widgetComponentProps}
          // TV1-2291 (t21) — this component IS edit mode (`WidgetLayoutComponent`
          // renders it or the viewer, never both), so the flag is a constant here.
          // `spacer` is the only consumer: it shows an author-only placeholder,
          // and renders nothing at all for a reader.
          editMode
        />
      ),
      mobile: ( w ) => <MobileWidget
          widget={w}
          data={widgetDataMap[w.definition.id]}
          type={w.definition.type}
          widgetComponentProps={w.definition.widgetComponentProps}
        />
    };
  
    const renderComponent = deviceComponents[device];
    return renderComponent ? renderComponent( widget ) : <>{t( 'unsupported_device','unsupported device' )}</>;
  };
  return (
    <div className={classes.shell}>
      {/* RETAINED (TV1-2291 · t17). The builder modal is gone, this one is not:
          removal is destructive and irreversible, and the inspector's "Quitar
          tarjeta" routes through exactly this dialog rather than round a second
          way. It renders NOTHING until a widget is staged for deletion, so the
          editing surface itself carries no dialog and no overlay. */}
      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        widget={widgetToDelete}
      />
      {/*
        ⚠️ HIDDEN WHILE THE PANEL IS OPEN (2026-08-18).
        
        The floating bar is centred on the CANVAS, but the docked panel is out of
        flow over the canvas's right edge — so at narrower widths the two overlap
        and the last palette icons sit underneath the panel, unclickable.
        
        Hiding the bar rather than moving it is the right trade: while the panel is
        open you are CONFIGURING a card, not adding one, so the bar has nothing to
        offer that moment. Closing the panel brings it straight back.
      */}
      {canEdit && !inspectorOpen ? (
        <WidgetToolbar
          available={availableWidgets}
          onAddWidget={( seed ) => { if ( seed?.type ) addWidgetOfType( seed.type as WidgetType ); }}
        />
      ) : (
        // Read-only canvas: no palette (adding a card is a mutation too), no
        // per-card actions, no drag, no resize — just the report and a line
        // saying why it will not move.
        <div
          className={classes.readOnlyNote}
          data-testid={componentsTestIds.widgetEditorReadOnlyNote}
        >
          <IconInfoCircle size={16} className={classes.readOnlyNoteIcon} />
          <div>
            <Text className={classes.readOnlyNoteTitle}>
              {t( 'widget_editor_read_only_title', 'Editing needs a wider screen' )}
            </Text>
            <Text className={classes.readOnlyNoteDescription}>
              {t(
                'widget_editor_read_only_description',
                'Below {{minWidth}}px the report is read-only. Widen the window to move, resize or add cards.',
                { minWidth: EDIT_MIN_CANVAS_PX }
              )}
            </Text>
          </div>
        </div>
      )}
      <div className={classes.editor} ref={editorRef}>
        {/* The way into report settings, now that the panel is not always there.
            Hidden while the panel IS open — it would be a button to open what is
            already open, sitting on top of it. */}
        {canEdit && !inspectorOpen ? (
          <Tooltip label={t( 'widget_editor_report_settings', 'Ajustes del informe' )} position="left">
            <ActionIcon
              size="lg"
              radius="md"
              variant="default"
              className={classes.reportSettings}
              data-testid={componentsTestIds.reportsInspectorOpenSettings}
              aria-label={t( 'widget_editor_report_settings', 'Ajustes del informe' )}
              onClick={() => setReportPaneOpen( true )}
            >
              <IconSettings size={16} />
            </ActionIcon>
          </Tooltip>
        ) : null}
        <div className={classes.scroller} ref={scrollerRef} onClick={handleBackgroundClick}>
        <div
          ref={wrapperRef}
          className={`${classes.canvas} ${canEdit ? classes.grid : ''}`}
          style={{
            'width': geometry.canvasWidthPx, // no x scroll
            'backgroundSize': `${colPx}px ${rowPx}px`,
            // The card gutter, from the REPORT (see `.card` in WidgetCard.module.css
            // for why the editor opts in through a custom property while the viewer
            // gets its inset from `widgetRectPx`).
            // @ts-ignore
            '--widget-gap': `${geometry.gapPx}px`,
            // @ts-ignore
            '--grid-col': `${colPx}px`,
            // @ts-ignore
            '--grid-row': `${rowPx}px`
          }}
        >
          {/* TV1-2291 (t10) — THE DROP GHOST. It marks the RESOLVED landing cell
              (post-swap, post-compaction) rather than the pointer position, so
              what the user sees before releasing is what they get. Rendered
              BEFORE the cards so it paints underneath them, and inert. */}
          {ghostRect ? (
            <div
              data-testid={componentsTestIds.widgetDropGhost}
              data-swapping={preview?.swapTargetId ? 'true' : undefined}
              className={`${classes.dropGhost} ${preview?.swapTargetId ? classes.dropGhostSwapping : ''}`}
              style={ghostRect}
            >
              {preview?.swapTargetId ? (
                <span className={classes.dropGhostTag}>
                  {t( 'widget_editor_swap_ghost_label', 'intercambiar' )}
                </span>
              ) : null}
            </div>
          ) : null}
          {/* The DOM renders the DISPLAY resolution — clamped to the legible
              minimums for this container width. Nothing here is ever persisted;
              `updateWidget` writes to the AUTHORED array by `definition.id`.
              Mid-gesture, every card EXCEPT the dragged one renders its previewed
              cell instead (`renderRectOf`) — that is the live push/compact. */}
          {displayWidgets.map(( displayed ) => {
            const w = renderRectOf( displayed );
            const isSwapTarget = preview?.swapTargetId === w.definition?.id;
            return (
            // Inside the return statement, update the Rnd component
            <Rnd
              key={w.definition?.id}
              data-testid={componentsTestIds.widgetRnd( w.definition?.id )}
              // Not a testid — a rendered STATE. The AC is "the swap target is
              // visually distinguished while armed", and an attribute that
              // appears exactly when the amber ring does is the honest assertion
              // for it (a class name is hashed by CSS modules).
              data-swap-target={isSwapTarget ? 'true' : undefined}
              className={`${classes.widgetContainer} ${isInteracting ? classes.active : ''} ${isSwapTarget ? classes.swapTarget : ''}`}
              size={{ width: w.width * colPx, height: w.height * rowPx }}
              position={{ x: w.x * colPx, y: w.y * rowPx }}
              // bounds="parent"
              grid={snapGrid}
              disableDragging={!canEdit}
              enableResizing={canEdit ? { right: true, bottom: true, bottomRight: true } : false}
              style={{ display: 'flex', overflow: 'hidden' }}
              // TV1-2291 (t17) — CLICK TO SELECT. `<Rnd>` forwards unknown props
              // to its root (the same mechanism `data-widget-frame` relies on),
              // so this covers the whole frame: the card, its action bar and its
              // grips.
              //
              // ⚠️ It SWITCHES an open panel and never opens one — see
              // `handleCardClick`. A drag ends in a click too, which is the right
              // outcome while configuring (the card you just moved is the one you
              // are working on) and correctly does nothing when you were only
              // rearranging. Absent on a read-only canvas, where there is no panel.
              onClick={canEdit ? () => handleCardClick( w.definition?.id ) : undefined}
              // TV1-2291 (t12) — the grips are the CHROME's, not three inline
              // `#3498db` divs. `resizeHandleStyles` normalises re-resizable's
              // hit areas (its defaults hang them half outside the card, which
              // put a card's right grip on top of its neighbour the moment they
              // became visible); `widgetResizeHandles` draws them, and keeps the
              // ticket-10 testids that are the resize branch's only oracle.
              {...WIDGET_FRAME_PROPS}
              resizeHandleStyles={WIDGET_RESIZE_HANDLE_STYLES}
              resizeHandleComponent={widgetResizeHandles( w.definition?.id )}
              onDragStart={beginGesture}
              // Pointer PIXELS convert to CELLS against the DISPLAY geometry —
              // that is the grid the user is actually pointing at, and `w.width`
              // here is the displayed (possibly clamped) span, so the drag stays
              // inside the canvas. The resulting cells then apply to the
              // AUTHORED widget: cells are the common currency of the two
              // resolutions, and placement runs on the authored array.
              onDrag={( e, d ) => {
                ensureCanvasHeight( d.y + w.height * rowPx );
                previewGesture(
                  w.definition?.id,
                  {
                    x: pxToCol( d.x, geometry, w.width ),
                    y: pxToRow( d.y, geometry ),
                    ...authoredSpanOf( w.definition?.id )
                  },
                  true
                );
                // --- Auto-scroll logic ---
                if ( e && 'clientY' in e ) {
                  handleAutoScroll( e.clientY );
                }
              }}
              onResizeStart={beginGesture}
              onResize={( e, __, ref, ___, pos ) => {
                ensureCanvasHeight( pos.y + ref.offsetHeight );
                previewGesture(
                  w.definition?.id,
                  {
                    x: pxToCol( pos.x, geometry, pxToSpanX( ref.offsetWidth, geometry )),
                    y: pxToRow( pos.y, geometry ),
                    width: pxToSpanX( ref.offsetWidth, geometry ),
                    height: pxToSpanY( ref.offsetHeight, geometry )
                  },
                  // A resize is not a statement about WHERE the card should go, so
                  // it never swaps — it only pushes. (The prototype hard-codes
                  // `{ target: null }` on the resize branch for the same reason.)
                  false
                );
                if ( e && 'clientY' in e ) {
                  handleAutoScroll( e.clientY );
                }
              }}
              onDragStop={( _, d ) => {
                commitGesture(
                  w.definition?.id,
                  {
                    x: pxToCol( d.x, geometry, w.width ),
                    y: pxToRow( d.y, geometry ),
                    ...authoredSpanOf( w.definition?.id )
                  },
                  true
                );
              }}
              onResizeStop={( _, __, ref, ___, pos ) => {
                const nextW = pxToSpanX( ref.offsetWidth, geometry );
                const nextH = pxToSpanY( ref.offsetHeight, geometry );
                commitGesture(
                  w.definition?.id,
                  {
                    x: pxToCol( pos.x, geometry, nextW ),
                    y: pxToRow( pos.y, geometry ),
                    width: nextW,
                    height: nextH
                  },
                  false
                );
              }}
            >
              {/* TV1-2291 (t11/t12) — the card surface is SHARED with the viewer
                  (`WidgetCard`); only the INTERACTIVE chrome is passed in, through
                  the `chrome` slot, and it is now `WidgetCardActions` (tokenised,
                  dark-mode-correct) rather than two `color="blue"` / `color="red"`
                  ActionIcons declared here. `className` still carries this
                  renderer's own placement (`position: relative` inside the
                  `<Rnd>`); the bar's reveal-on-hover/focus rule hangs off the
                  card's `data-widget-card` attribute, so it no longer depends on
                  a class that happens to live in this module. */}
              <WidgetCard
                widget={w}
                // A colour STEP paints the whole card, so it is resolved where the widget
                // AND its fetched value are both in scope — here, not in the card.
                stepTint={resolveWidgetStepTint( w, widgetDataMap[w.definition?.id])}
                testId={componentsTestIds.reportsCanvasCard( w.definition?.id )}
                className={classes.widget}
                // TV1-2291 (t17) — the SOURCE ticket 12 left unwired. Purely
                // visual (an accent ring + the action bar pinned open); it grants
                // no affordance the `chrome` slot did not already carry, so the
                // viewer's read-only guarantee is untouched.
                selected={canEdit && w.definition?.id === selectedWidgetId}
                chrome={canEdit ? (
                  <WidgetCardActions
                    widgetId={w.definition?.id}
                    onEdit={() => selectWidget( w.definition?.id )}
                    onRemove={() => handleDeleteClick( w )}
                  />
                ) : null}
              >
                {renderWidgetForDevice( device, w )}
              </WidgetCard>
            </Rnd>
            );
          })}
        </div>
        </div>
        {/* TV1-2291 (t17 · SDD §3) — THE DOCKED INSPECTOR. Canvas left, panel
            right, both live: it is a plain `<aside>` in this flex row, with no
            overlay, no focus trap and no portal, so the report stays visible and
            re-renders as the card is configured. That is the entire reason the
            `WidgetBuilder` modal is no longer rendered from here.

            ⚠️ MOUNTED ON DEMAND (2026-08-17), not always. S16 docked it
            unconditionally and reserved its column with an `aria-hidden`
            placeholder, so an editor with nothing selected spent 348px saying
            "nothing is selected" — next to a floating bar that could already add
            cards. It now appears for a selected card or for report settings, and
            the loop that the placeholder existed to prevent is handled by
            measuring the row instead of the scroller (see the two-measurement
            note above). The placeholder is deleted, not merely unused. */}
        {inspectorOpen ? (
          // The dock takes the panel OUT OF FLOW — see `.inspectorDock` in the CSS
          // module for why that is load-bearing rather than cosmetic (in flow, the
          // panel was rendered 300px past the right edge of the screen).
          <div className={classes.inspectorDock}>
            <WidgetInspector
              pane={inspectorPane}
              onClose={closeInspector}
              layout={authoredLayout}
              onLayoutChange={handleInspectorLayoutChange}
              selectedWidget={selectedWidget?.definition ?? null}
              onWidgetChange={handleInspectorWidgetChange}
              tint={selectedWidget?.tint}
              onTintChange={handleTintChange}
              textColor={selectedWidget?.textColor}
              onTextColorChange={handleTextColorChange}
              onRemoveWidget={handleInspectorRemove}
              availableWidgets={availableWidgets}
              cubeCatalog={cubeCatalog}
              cubeMetaLoading={cubeMetaLoading}
              cubeMetaError={cubeMetaError}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}